import React, {useState, useEffect, useMemo} from "react";
import { useParams, useLocation } from "react-router-dom"; 
import Menu from "../../../../../components/layout/menu";
import SurveyHistoryTable from "../../../../../components/tables/surveys/surveyHistory";
import DownloadDocumentButton from "../../../../../components/shared/downloadDocument";
import { getResponseBySurvey, downloadResponsesSurvey } from "../../../../../client/surveys";
import ContentLoader from "react-content-loader";
import Pagination from "../../../../../components/shared/pagination";

export default function DetailsCompletedSurveys() {
  const { surveyId } = useParams();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const [surveyData, setSurveyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const surveyTitleFromState = location.state?.surveyTitle;

  useEffect(() => {
    const fetchSurveyResponses = async () => {
      if (!surveyId) return;
      
      try {
        setLoading(true);
        setError(null);
        const response = await getResponseBySurvey(
          parseInt(surveyId), 
          currentPage, 
          recordsPerPage
        );
                
        
        if (response.data && response.data.data) {
          setSurveyData(response.data.data);

          if (response.data.pagination) {
            setTotalPages(response.data.pagination.totalPages);
            setTotalCount(response.data.pagination.totalCount);
          }
        } else {
          setError("No se encontraron datos en la respuesta");
        }
      } catch (err) {
        console.error("Error completo:", err);
        setError(err.response?.data?.message || err.message || "Error al cargar las respuestas");
      } finally {
        setLoading(false);
      }
    };

    fetchSurveyResponses();
  }, [surveyId, currentPage, recordsPerPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRecordsCountChange = (e) => {
    const newRecordsPerPage = parseInt(e.target.value);
    setRecordsPerPage(newRecordsPerPage);
    setCurrentPage(1); 
  };
  
  const handleDownload = async () => {
    if (!surveyId) return;
    
    try {
      setDownloading(true);
      const response = await downloadResponsesSurvey(parseInt(surveyId));
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      let fileName = `Respuestas_${surveyId}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename\*?=UTF-8''([^;]+)/i);
        
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = decodeURIComponent(fileNameMatch[1]);
        } else {
          const simpleMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
          if (simpleMatch && simpleMatch[1]) {
            fileName = simpleMatch[1];
          }
        }
      }
      
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error("Error downloading survey responses:", error);
    } finally {
      setDownloading(false);
    }
  };

const getMinWidthForKey = (key) => {
  switch (key) {
    case 'Hora':
      return 100;
    case 'Respondió':
      return 120;
    default:
      return 150;
  }
};

const getWidthForKey = (key) => {
  switch (key) {
    case 'Hora':
      return 120;
    case 'Respondió':
      return 250;
    default:
      return 200;
  }
};

const columns = useMemo(() => {
  if (!surveyData || !surveyData.tableData || surveyData.tableData.length === 0) {
    return [];
  }

  const firstRow = surveyData.tableData[0];
  
  const dynamicColumns = Object.keys(firstRow).map(key => ({
    Header: key,
    accessor: (row) => {
      return row[key] || '';
    },
    id: key,
    minWidth: getMinWidthForKey(key),
    width: getWidthForKey(key)
  }));

  return dynamicColumns;
}, [surveyData]);



  const TableContentLoader = () => {
    const rows = 8;
    const rowHeight = 62;
    
  const getColumnWidths = () => {
    if (columns.length > 0) {
      return columns.map(col => col.width || col.minWidth || 150);
    }
    return Object.keys(surveyData?.tableData[0] || {}).map(() => 200);
  };

    const widths = getColumnWidths();
    const totalWidth = widths.reduce((sum, width) => sum + width, 0);
    
    const getXPositions = () => {
      const positions = [0];
      for (let i = 1; i < widths.length; i++) {
        positions.push(positions[i - 1] + widths[i - 1]);
      }
      return positions;
    };

    const xPositions = getXPositions();

    return (
      <div className="overflow-x-auto ">
        <ContentLoader 
          speed={2}
          width={totalWidth}
          height={rows * rowHeight}
          viewBox={`0 0 ${totalWidth} ${rows * rowHeight}`}
          backgroundColor="#f3f3f3"
          foregroundColor="#ecebeb"
        >
          {Array(rows).fill('').map((_, rowIndex) => (
            widths.map((width, colIndex) => (
              <rect 
                key={`${rowIndex}-${colIndex}`}
                x={xPositions[colIndex] + 10} 
                y={rowIndex * rowHeight + 20}
                rx="4" 
                ry="4" 
                width={width - 20} 
                height="20"
              />
            ))
          ))}
        </ContentLoader>
      </div>
    );
  };

  const finalSurveyTitle = surveyTitleFromState || surveyData?.survey?.title || 'nombre';

  return (
    <div>
      <Menu isMenuOpen={isMenuOpen} toggleMenu={toggleMenu} className="transition-all duration-300" />
        <div className={`flex-1 pt-[67px] transition-all duration-300 ${isMenuOpen ? "xl:pl-[20.7%] lg:pl-[23.7%] pl-0" : "pl-0"} bg-[#F7F8F9] w-full z-50 min-h-screen`}>
          <div className="sm:pt-8 pt-5 pb-10 sm:px-[3%] px-[5%] flex flex-col gap-4">
            <div className="flex flex-col items-start justify-between gap-2 sm:flex-row">
              <h1 className="text-blue font-itcbold text-2xl md:text-[32px]">
                Respuestas encuesta "{finalSurveyTitle}"
              </h1>
              <DownloadDocumentButton 
                text="Descargar"
                onClick={handleDownload}
                loading={downloading}
                hasData={surveyData?.tableData && surveyData.tableData.length > 0}
              />
            </div>

            {loading ? (
              <TableContentLoader />
            ) : 
            surveyData?.tableData && surveyData.tableData.length > 0 ? (
              <>
                <div className="flex items-center justify-between my-2">
                  <div className="flex items-center space-x-2 text-sm font-itcbook">
                    <p >Mostrar</p>
                    <div className="border-[#D1D5DB] border-[1px] rounded-md w-[58px] flex justify-center items-center h-[40px]">
                      <select
                        className="w-full h-full px-2 text-sm bg-white rounded-md outline-none cursor-pointer font-itcbook"
                        value={recordsPerPage}
                        onChange={handleRecordsCountChange}  
                      >
                        <option value={10}>10</option>
                        <option value={15}>15</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                      </select>
                    </div>
                    <p >Registros</p>
                  </div>
                </div>
                <SurveyHistoryTable 
                  columns={columns}
                  data={surveyData.tableData}
                  emptyMessage="No hay respuestas para esta encuesta"
                />
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  changePage={handlePageChange}
                  recordsPerPage={recordsPerPage}
                  totalRecords={totalCount}
                />
              </>
            ) : (
              <div className="py-8 text-center text-gray-500">
                No hay respuestas para esta encuesta
              </div>
            )}
          </div>
        </div>
    </div>
  );
}